import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Autoplay from "embla-carousel-autoplay";
import { Feather, HandHeart, Users, Sparkles as SparklesIcon, Sparkles, ArrowRight, Gift, Quote, Leaf, Flame, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import Seo from "@/components/Seo";
import { http } from "@/lib/api";
import { LatestVideosSection } from "@/components/LatestVideosSection";

const LOGO = "/brand/logo.png";

// Carousel component for testimonials (auto-play with pause on hover)
function TestimonialsCarousel({ testimonials }) {
    const autoplayRef = useRef(
        Autoplay({ delay: 6000, stopOnInteraction: false, stopOnMouseEnter: true })
    );
    const fmtMonth = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        if (isNaN(d)) return "";
        return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    };
    return (
        <Carousel
            opts={{ align: "start", loop: testimonials.length > 3, startIndex: 0 }}
            plugins={[autoplayRef.current]}
            className="w-full"
            data-testid="testimonials-carousel"
        >
            <CarouselContent className="-ml-4">
                {testimonials.map((t) => (
                    <CarouselItem key={t.id} className="pl-4 md:basis-1/2 lg:basis-1/3" data-testid={`testimonial-${t.id}`}>
                        <blockquote className="relative rounded-3xl bg-card border border-secondary/30 p-8 flex flex-col shadow-sm h-full">
                            {typeof t.rating === "number" && t.rating > 0 && (
                                <div className="flex items-center gap-0.5 mb-4" data-testid={`testimonial-rating-${t.id}`}>
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star key={n} className={`w-4 h-4 ${n <= t.rating ? "fill-secondary text-secondary" : "text-muted-foreground/40"}`} />
                                    ))}
                                </div>
                            )}
                            <p className="text-base leading-relaxed text-foreground/85 flex-1 mb-6 italic max-h-[340px] overflow-y-auto pr-2 [scrollbar-width:thin]">
                                « {t.content} »
                            </p>
                            <footer className="flex items-center gap-3 pt-4 border-t border-border/40">
                                <div className="w-9 h-9 rounded-full bg-secondary/20 flex items-center justify-center text-secondary font-serif text-lg shrink-0">
                                    {(t.display_name || "?").charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <cite className="not-italic text-sm font-medium block truncate">
                                        {t.display_name}
                                    </cite>
                                    {t.created_at && (
                                        <time
                                            dateTime={t.created_at}
                                            className="text-[11px] uppercase tracking-widest text-muted-foreground"
                                            data-testid={`testimonial-date-${t.id}`}
                                        >
                                            {fmtMonth(t.created_at)}
                                        </time>
                                    )}
                                </div>
                            </footer>
                        </blockquote>
                    </CarouselItem>
                ))}
            </CarouselContent>
            {testimonials.length > 3 && (
                <>
                    <CarouselPrevious className="hidden md:flex -left-4 lg:-left-12 bg-card border-secondary/40 text-secondary hover:bg-secondary hover:text-secondary-foreground" />
                    <CarouselNext className="hidden md:flex -right-4 lg:-right-12 bg-card border-secondary/40 text-secondary hover:bg-secondary hover:text-secondary-foreground" />
                </>
            )}
        </Carousel>
    );
}

const services = [
    {
        icon: HandHeart,
        title: "Soins de lumière",
        text: "Soins énergétiques avec le langage de lumière — un canal doux et puissant pour rééquilibrer votre système énergétique en profondeur.",
        img: "https://images.unsplash.com/photo-1447619297994-b829cc1ab44a?w=800",
        span: "md:col-span-7 md:row-span-2",
    },
    {
        icon: Feather,
        title: "Connexion à vos guides",
        text: "Recevez des séquences de langage de lumière personnalisées et des messages canalisés de vos guides.",
        img: "https://images.unsplash.com/photo-1494386346843-e12284507169?w=600",
        span: "md:col-span-5",
    },
    {
        icon: SparklesIcon,
        title: "Accompagnements",
        text: "Un chemin sacré pour éclairer les questions de la vie et retrouver progressivement sa cohérence intérieure.",
        img: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600",
        span: "md:col-span-5",
    },
];

const eventSeries = [
    {
        title: "Harmonisation de l'être",
        text: "Poser les fondations : équilibrer les énergies, apaiser le mental et retrouver un ancrage joyeux dans le corps.",
        img: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=900",
        icon: Leaf,
    },
    {
        title: "Éveil de conscience",
        text: "Ouvrir le regard intérieur : reconnaître ce qui vit en soi, dialoguer avec ses guides et incarner sa vérité vibratoire.",
        img: "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=900",
        icon: SparklesIcon,
    },
    {
        title: "Libération de l'Être",
        text: "Traverser et dénouer : libérer les charges anciennes, les mémoires denses, pour laisser advenir une nouvelle amplitude.",
        img: "https://images.unsplash.com/photo-1447619297994-b829cc1ab44a?w=900",
        icon: Flame,
    },
];

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

export default function Landing() {
    const [circleVoices, setCircleVoices] = useState([]);      // curated community_posts (subscribers content)
    const [testimonials, setTestimonials] = useState([]);
    const [articles, setArticles] = useState([]);      // user-submitted testimonials with ratings (public)
    const location = useLocation();

    // Subtle parallax on the mobile hero photo — the Earth shifts slightly downwards
    // as the visitor scrolls, giving the image a sense of depth without being distracting.
    const { scrollY } = useScroll();
    const heroParallaxY = useTransform(scrollY, [0, 600], [0, 90]);

    useEffect(() => {
        http.get("/community/featured").then((r) => setCircleVoices(r.data)).catch(() => {});
        http.get("/testimonials/featured").then((r) => {
            // Safety sort: newest first by created_at (API already orders, but
            // defend against stale caches or bulk-seed anomalies).
            const sorted = [...(r.data || [])].sort((a, b) => {
                const da = new Date(a.created_at || 0).getTime();
                const db = new Date(b.created_at || 0).getTime();
                return db - da;
            });
            setTestimonials(sorted);
        }).catch(() => {});
        http.get("/articles").then((r) => setArticles(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.slice(1);
            // Delay to let sections mount + animations settle
            const t = setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 150);
            return () => clearTimeout(t);
        }
    }, [location.hash]);

    return (
        <div data-testid="landing-page">
            <Seo
                title="Foundation of Light · Langage de lumière & transmissions"
                description="Le langage de lumière, canal vibratoire de soins énergétiques et d'éveil de conscience. Séances à distance, ateliers en ligne et cercles sacrés."
                path="/"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "Foundation of Light",
                        "url": "https://foundation-of-light.com",
                        "logo": "https://foundation-of-light.com/brand/logo.png",
                        "description": "Sanctuaire spirituel dédié au langage de lumière — canal vibratoire de soins énergétiques, transmissions et éveil de conscience.",
                        "email": "info@foundation-of-light.com",
                        "founder": {
                            "@type": "Person",
                            "name": "Olivier Masurel",
                            "url": "https://foundation-of-light.com/histoire",
                        },
                        "sameAs": [
                            "https://www.facebook.com/profile.php?id=61594037591876",
                            "https://www.youtube.com/channel/UCmu2akD8eUBRIeD5d-Vmwfg",
                            "https://www.tiktok.com/@foundation.of.light",
                        ],
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "serviceType": "Langage de lumière — soins énergétiques",
                        "name": "Le langage de lumière — Foundation of Light",
                        "provider": {
                            "@type": "Organization",
                            "name": "Foundation of Light",
                            "url": "https://foundation-of-light.com",
                        },
                        "areaServed": "Worldwide",
                        "description": "Séances individuelles et ateliers collectifs de langage de lumière — un canal vibratoire de soins énergétiques, guidance et éveil de conscience à distance.",
                    },
                ]}
            />
            {/* Hero — Foundation of Light / Earth background */}
            {/* Positionnement responsive :
                - Mobile : object-cover avec object-position ajusté pour garder les rayons de lumière au centre du viewport.
                - Desktop : image tirée sous le section-bottom pour ancrer le milieu de la Terre en bas de la section
                  (image ratio ≈ 0.558, milieu Terre à 84.5% depuis le haut). */}
            <section className="relative flex flex-col overflow-hidden bg-[#0b1424]" style={{ minHeight: "100vh" }}>
                <div className="absolute inset-0 pointer-events-none">
                    {/* Mobile background — cover fills the whole hero so no solid block above the text.
                        Subtle parallax: image slides gently down as the visitor scrolls. */}
                    <motion.img
                        src="https://customer-assets-jai6qajn.emergentagent.net/job_soul-retreat-2/artifacts/qrkw3t2w_fotor_creation_2026-08-14.jpg"
                        alt="Terre vue depuis l'espace, symbole cosmique du langage de lumière — Foundation of Light"
                        className="md:hidden absolute inset-0 w-full h-full object-cover will-change-transform"
                        style={{ objectPosition: "center 70%", y: heroParallaxY }}
                    />
                    {/* Desktop background — anchored to bottom so Earth's centre sits at section bottom */}
                    <img
                        src="https://customer-assets-jai6qajn.emergentagent.net/job_soul-retreat-2/artifacts/qrkw3t2w_fotor_creation_2026-08-14.jpg"
                        alt="Terre vue depuis l'espace, symbole cosmique du langage de lumière — Foundation of Light"
                        className="hidden md:block absolute inset-x-0 w-full h-auto"
                        style={{ bottom: "-27.8vw" }}
                    />
                    {/* Dark gradient overlay — stronger on mobile so text stays readable over the photo */}
                    <div className="absolute inset-x-0 top-0 h-2/3 md:h-1/3 bg-gradient-to-b from-[#0b1424] via-[#0b1424]/80 md:via-[#0b1424]/70 to-transparent" />
                </div>
                <div className="relative flex-1 flex items-center max-w-7xl w-full mx-auto px-6 py-24">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="max-w-2xl">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-20 h-20 rounded-full bg-amber-200 ring-1 ring-amber-200/60 shadow-md flex items-center justify-center">
                                <img
                                    src={LOGO}
                                    alt="Foundation of Light"
                                    width="80"
                                    height="80"
                                    loading="eager"
                                    decoding="async"
                                    className="w-[85%] h-[85%] object-contain"
                                />
                            </div>
                            <div className="text-xs tracking-[0.35em] uppercase text-white/70 font-medium">
                                Foundation<br />of Light
                            </div>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight mb-8 text-white">
                            Recevoir<br />
                            <span className="italic text-amber-200">la lumière</span><br />
                            en soi.
                        </h1>
                        <p className="text-lg text-white/75 max-w-lg mb-8 leading-relaxed">
                            Cercles sacrés d'éveil de conscience et de soins en groupe, séances individuelles de soins énergétiques avec le langage de lumière (alias soins de lumière)
                            et accompagnements — un sanctuaire en ligne pour cheminer vers l'harmonie et la cohérence intérieure.
                        </p>
                        <p className="text-base text-white/60 max-w-lg mb-4 leading-relaxed italic">
                            Un pont vivant entre le visible et l'invisible, un espace de résonance pour celles et ceux
                            qui ressentent que l'humanité s'éveille à quelque chose de plus grand.
                        </p>
                        <Link
                            to="/a-propos"
                            className="inline-flex items-center gap-1.5 text-sm text-amber-200 hover:text-amber-100 underline decoration-amber-200/40 underline-offset-4 hover:decoration-amber-100 transition-colors mb-10"
                            data-testid="hero-apropos-link"
                        >
                            En savoir plus sur la Foundation of Light
                            <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/abonnements">
                                <Button className="rounded-full h-14 px-8 text-base bg-white text-[#0b1424] hover:bg-amber-100 hover:scale-105 transition-transform" data-testid="hero-cta-primary">
                                    Découvrir les abonnements
                                    <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/reservation">
                                <Button variant="outline" className="rounded-full h-14 px-8 text-base bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white" data-testid="hero-cta-secondary">
                                    Réserver un soin
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Foundation of Light — Manifesto moved to /a-propos */}

            {/* Mon histoire — teaser */}
            <section id="mon-histoire" className="relative bg-accent/40 py-32 scroll-mt-24" data-testid="story-teaser">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.8 }}
                        className="relative aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-border/60"
                    >
                        <img
                            src="https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?w=900"
                            alt="Réception du langage de lumière des hautes sphères de conscience diffusé via les cercles sacrés de transmission de la Foundation of Light"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                    >
                        <div className="text-sm tracking-[0.3em] uppercase text-primary mb-4 font-medium">Le chemin qui a mené ici</div>
                        <h2 className="font-serif text-4xl sm:text-5xl mb-6">
                            Mon histoire — <span className="italic">un rappel de la lumière.</span>
                        </h2>
                        <p className="text-lg text-foreground/75 leading-relaxed mb-4">
                            Ce que je transmets aujourd'hui prend racine dans un parcours singulier, tissé de reconnexions,
                            d'ouvertures et de rencontres avec les plans supérieurs de conscience.
                        </p>
                        <p className="text-base text-foreground/70 leading-relaxed mb-4">
                            Un chemin qu'il me tient à cœur de partager avec celles et ceux qui ressentent, eux aussi,
                            que quelque chose de plus vaste les appelle.
                        </p>
                        <p
                            className="text-sm text-primary/80 italic mb-10 tracking-wide"
                            itemScope
                            itemType="https://schema.org/Person"
                            data-testid="landing-story-signature"
                        >
                            — <strong itemProp="name" className="font-medium not-italic">Olivier Masurel</strong>, fondateur de la Foundation of Light
                            <meta itemProp="url" content="https://foundation-of-light.com/histoire" />
                        </p>
                        <Link to="/histoire">
                            <Button className="rounded-full h-12 px-7 bg-primary text-primary-foreground hover:scale-[1.03] transition-transform" data-testid="story-cta">
                                Découvrir mon histoire
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Nos évènements */}
            <section className="max-w-7xl mx-auto px-6 py-32" data-testid="events-section">
                <div className="mb-16">
                    <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Nos évènements</div>
                    <h2 className="font-serif text-4xl sm:text-5xl">Trois séries d'ateliers pour cheminer</h2>
                    <p className="text-lg text-foreground/70 mt-4 max-w-2xl">
                        Chaque série offre une porte d'entrée singulière — un axe d'attention pour approfondir votre chemin intérieur, mois après mois.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:h-[560px]">
                    {eventSeries.map((e, i) => (
                        <motion.div
                            key={e.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className="relative overflow-hidden rounded-3xl border border-border/60 group"
                            data-testid={`event-series-${i}`}
                        >
                            <img src={e.img} alt={e.alt || `Série d'ateliers ${e.title} — Foundation of Light`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
                            <div className="relative h-full flex flex-col justify-end p-8">
                                <e.icon className="w-6 h-6 text-secondary mb-3" />
                                <h3 className="font-serif text-3xl mb-2">{e.title}</h3>
                                <p className="text-sm text-foreground/70 max-w-sm">{e.text}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
                <div className="mt-10 flex flex-wrap gap-4">
                    <Link to="/evenements">
                        <Button className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:scale-[1.02] transition-transform" data-testid="events-see-all">
                            Voir tous les ateliers à venir <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    </Link>
                    <Link to="/abonnements">
                        <Button variant="outline" className="rounded-full h-12 px-6" data-testid="events-see-plans">
                            Découvrir les abonnements
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Journal — articles carousel (SEO/AI discovery + engagement) */}
            {articles.length > 0 && (
                <section className="bg-accent/30 border-y border-border/40 py-24" data-testid="landing-articles-section">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
                            <div>
                                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium flex items-center gap-2">
                                    <Feather className="w-4 h-4" /> Journal
                                </div>
                                <h2 className="font-serif text-4xl sm:text-5xl">Réflexions & transmissions</h2>
                            </div>
                            <Link to="/journal">
                                <Button variant="outline" className="rounded-full" data-testid="landing-articles-see-all">
                                    Tous les articles <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                        </div>
                        <Carousel opts={{ align: "start", loop: articles.length > 3 }} className="w-full" data-testid="articles-carousel">
                            <CarouselContent className="-ml-4">
                                {articles.map((a) => (
                                    <CarouselItem key={a.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                                        <Link to={`/journal/${a.slug}`} className="block rounded-3xl border border-border/60 bg-card overflow-hidden group hover:shadow-xl transition-shadow h-full" data-testid={`landing-article-card-${a.slug}`}>
                                            {a.image_url && (
                                                <div className="aspect-[16/10] overflow-hidden">
                                                    <img src={a.image_url} alt={a.image_alt || a.title} title={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                                                </div>
                                            )}
                                            <div className="p-6">
                                                <h3 className="font-serif text-xl md:text-2xl mb-2 group-hover:text-primary transition-colors line-clamp-2">{a.title}</h3>
                                                <p className="text-sm text-foreground/70 leading-relaxed mb-4 line-clamp-4">{a.excerpt}</p>
                                                <span className="inline-flex items-center gap-2 text-sm text-primary font-medium">
                                                    Voir l'article complet <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                                </span>
                                            </div>
                                        </Link>
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                            {articles.length > 1 && (
                                <>
                                    <CarouselPrevious className="hidden md:flex -left-4" />
                                    <CarouselNext className="hidden md:flex -right-4" />
                                </>
                            )}
                        </Carousel>
                    </div>
                </section>
            )}

            {/* Découverte — dernières vidéos YouTube + TikTok */}
            <LatestVideosSection />

            {/* Services */}
            <section className="max-w-7xl mx-auto px-6 pb-32">
                <div className="mb-16">
                    <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Nos pratiques</div>
                    <h2 className="font-serif text-4xl sm:text-5xl">Le langage de lumière, soins énergétiques & guidance</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 md:grid-rows-2 gap-6 md:h-[640px]">
                    {services.map((s, i) => (
                        <motion.div
                            key={s.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className={`relative overflow-hidden rounded-3xl border border-border/60 group ${s.span}`}
                            data-testid={`service-${s.title.toLowerCase()}`}
                        >
                            <img src={s.img} alt={`${s.title} — pratique énergétique Foundation of Light`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
                            <div className="relative h-full flex flex-col justify-end p-8">
                                <s.icon className="w-6 h-6 text-secondary mb-3" />
                                <h3 className="font-serif text-3xl mb-2">{s.title}</h3>
                                <p className="text-sm text-foreground/70 max-w-sm">{s.text}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
                <div className="mt-10 flex flex-wrap gap-4">
                    <Link to="/reservation">
                        <Button className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:scale-[1.02] transition-transform" data-testid="services-book-cta">
                            Réserver un soin <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    </Link>
                    <Link to="/accompagnements">
                        <Button variant="outline" className="rounded-full h-12 px-6" data-testid="services-accompaniments-cta">
                            Découvrir les accompagnements <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Testimonials — customer reviews with star ratings (carousel with auto-play) */}
            {testimonials.length > 0 && (
                <section className="bg-secondary/5 border-y border-secondary/20 py-24" data-testid="testimonials-section">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="mb-12 text-center max-w-2xl mx-auto">
                            <div className="text-sm tracking-[0.25em] uppercase text-secondary-foreground/70 mb-4 font-medium">Témoignages</div>
                            <h2 className="font-serif text-4xl sm:text-5xl mb-4">Ce qu'ils en disent</h2>
                            <p className="text-base text-foreground/70">Les mots de celles et ceux qui ont franchi le pas.</p>
                        </div>
                        <TestimonialsCarousel testimonials={testimonials} />
                    </div>
                </section>
            )}

            {/* Voix du cercle — curated posts from the community feed (admin-selected) */}
            {circleVoices.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 pb-32 pt-24" data-testid="circle-voices-section">
                    <div className="mb-14 max-w-2xl">
                        <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Voix du cercle</div>
                        <h2 className="font-serif text-4xl sm:text-5xl mb-4">Ce que la communauté partage</h2>
                        <p className="text-base text-foreground/70">Extraits sélectionnés du flux de la communauté privée.</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {circleVoices.slice(0, 6).map((c, i) => (
                            <motion.blockquote
                                key={c.id}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
                                className="relative rounded-3xl border border-border/60 bg-card p-8 flex flex-col"
                                data-testid={`circle-voice-${c.id}`}
                            >
                                <Quote className="w-6 h-6 text-primary/70 mb-4" />
                                <p className="text-base leading-relaxed text-foreground/85 flex-1 mb-6">
                                    {c.content.length > 260 ? c.content.slice(0, 260).trimEnd() + "…" : c.content}
                                </p>
                                <footer className="flex items-center gap-3 pt-4 border-t border-border/40">
                                    <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary font-serif text-lg">
                                        {(c.user_name || "?").charAt(0)}
                                    </div>
                                    <cite className="not-italic text-sm font-medium">
                                        {c.user_name}
                                    </cite>
                                </footer>
                            </motion.blockquote>
                        ))}
                    </div>
                </section>
            )}

            {/* Community */}
            <section className="bg-accent/40 py-32">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
                    <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
                        <img src="https://images.unsplash.com/photo-1643682661119-28da0685be2c?w=900" alt="Communauté Foundation of Light — cercle de membres unis autour d'une pratique énergétique de langage de lumière" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div>
                        <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Communauté</div>
                        <h2 className="font-serif text-4xl sm:text-5xl mb-6">
                            Un cercle vivant<br /><span className="italic">pour cheminer ensemble.</span>
                        </h2>
                        <p className="text-lg text-foreground/70 mb-8 leading-relaxed">
                            Deux abonnements au choix. Chacun donne l'accès gratuit à une série d'ateliers —
                            « Harmonisation de l'être » (22 €/mois) ou « Éveil de conscience » (47 €/mois) —
                            et inclut automatiquement -33 % sur tous les autres ateliers (Libération de l'Être, Unification de l'âme, ou l'autre série d'abonnement), tant que votre abonnement est actif.
                            L'abonnement ouvre aussi l'accès à la communauté en ligne et aux ressources exclusives.
                        </p>
                        <div className="relative rounded-3xl border border-secondary/40 bg-secondary/10 p-6 mb-8" data-testid="community-mission">
                            <div className="absolute -top-3 left-6 bg-secondary text-secondary-foreground text-[10px] uppercase tracking-widest px-3 py-1 rounded-full">
                                Notre mission
                            </div>
                            <p className="text-base leading-relaxed text-foreground/85 italic">
                                En vous abonnant, vous contribuez directement aux travaux de diffusion de la
                                <strong className="not-italic font-semibold"> Foundation of Light</strong> —
                                pour semer, ensemble, les graines de l'harmonisation planétaire et de l'éveil
                                d'une nouvelle conscience. Chaque adhésion devient un acte d'entraide vibratoire
                                à l'échelle du monde.
                            </p>
                        </div>
                        <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
                            Cette remise ne s'applique pas aux soins de lumière, aux séances de guidance, ni aux accompagnements.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/abonnements">
                                <Button className="rounded-full h-12 px-6 bg-secondary text-secondary-foreground hover:scale-105 transition-transform" data-testid="community-cta">
                                    <Users className="w-4 h-4 mr-2" /> Rejoindre la communauté
                                </Button>
                            </Link>
                            <Link to="/evenements">
                                <Button variant="outline" className="rounded-full h-12 px-6 border-primary/40" data-testid="workshop-cta">
                                    <Sparkles className="w-4 h-4 mr-2" /> Participer à un atelier
                                </Button>
                            </Link>
                            <Link to="/offrir">
                                <Button variant="outline" className="rounded-full h-12 px-6 border-primary/40" data-testid="gift-cta">
                                    <Gift className="w-4 h-4 mr-2" /> Offrir un soin
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Welcome gift — newsletter replay offered */}
            <WelcomeGiftLandingBlock />
        </div>
    );
}

// ---------- Welcome gift block (visible only when admin has configured a replay) ----------
function WelcomeGiftLandingBlock() {
    const [gift, setGift] = React.useState(null);
    const [firstName, setFirstName] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const [done, setDone] = React.useState(false);

    React.useEffect(() => {
        http.get("/newsletter/welcome-gift")
            .then((r) => setGift(r.data))
            .catch(() => {});
    }, []);

    if (!gift || !gift.is_active) return null;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await http.post("/newsletter/subscribe", {
                email: email.trim().toLowerCase(),
                name: firstName.trim() || null,
                source: "landing_welcome_gift",
            });
            setDone(true);
        } catch (err) {
            console.warn(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="bg-gradient-to-b from-[#f0ede4] to-background py-24" data-testid="landing-welcome-gift">
            <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                <div className="relative aspect-video rounded-3xl overflow-hidden shadow-xl border border-[#c9a66b]/40">
                    {gift.cover_url ? (
                        <img src={gift.cover_url} alt={gift.title || "Replay atelier offert"} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                            <Gift className="w-20 h-20 text-primary/60" strokeWidth={1.2} />
                        </div>
                    )}
                    <div className="absolute top-4 left-4 bg-[#c9a66b] text-white text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full font-semibold shadow-lg">
                        🎁 Cadeau offert
                    </div>
                </div>
                <div>
                    <div className="text-sm tracking-[0.25em] uppercase text-[#c9a66b] mb-4 font-semibold">Bienvenue à l'infolettre</div>
                    <h2 className="font-serif text-4xl sm:text-5xl mb-6">{gift.title || "Un replay offert"}</h2>
                    {gift.description && (
                        <p className="text-lg text-foreground/70 leading-relaxed mb-6">{gift.description}</p>
                    )}
                    {gift.duration_minutes && (
                        <p className="text-sm text-muted-foreground mb-6 inline-flex items-center gap-2">
                            <SparklesIcon className="w-4 h-4 text-[#c9a66b]" />
                            Durée du replay : <strong>{gift.duration_minutes} minutes</strong>
                        </p>
                    )}
                    {done ? (
                        <div className="rounded-2xl border-2 border-primary/40 bg-primary/5 p-6 text-center" data-testid="landing-welcome-gift-done">
                            <p className="font-serif text-2xl mb-2">Merci — c'est confirmé ✨</p>
                            <p className="text-sm text-foreground/70 mb-4">Le replay vient de vous être envoyé par email. Vous pouvez aussi l'ouvrir tout de suite.</p>
                            <Link to="/cadeau-bienvenue">
                                <Button className="rounded-full h-12 px-8 bg-primary text-primary-foreground" data-testid="landing-welcome-gift-open">
                                    Accéder au replay
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="rounded-2xl border border-[#c9a66b]/40 bg-card p-6 space-y-3 max-w-md" data-testid="landing-welcome-gift-form">
                            <input
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="Votre prénom (facultatif)"
                                className="w-full h-11 rounded-full border border-border/60 bg-background px-4 text-sm"
                                data-testid="landing-welcome-gift-name"
                            />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="votre@email.com"
                                className="w-full h-11 rounded-full border border-border/60 bg-background px-4 text-sm"
                                required
                                data-testid="landing-welcome-gift-email"
                            />
                            <Button type="submit" disabled={busy} className="w-full rounded-full h-12 bg-[#c9a66b] hover:bg-[#b89250] text-white shadow-lg" data-testid="landing-welcome-gift-submit">
                                {busy ? "Inscription…" : "🎁 Recevoir le replay gratuitement"}
                            </Button>
                            <p className="text-[11px] text-center text-muted-foreground">
                                Inscription à l'infolettre. Désinscription en 1 clic. <a href="/confidentialite" className="underline">Confidentialité</a>.
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
